import { useConfirmationDialogContext } from "@shared";
import { useAlertContext } from "@shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { CaseNoteResponse, ClientDetailResponse, caseNotesApi } from "@/api";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

export function useCaseNoteDelete(client: ClientDetailResponse, onDeleted: () => void) {
  const { t } = useTranslation();
  const { showSuccess, showError } = useAlertContext();
  const { showYesNoDialog } = useConfirmationDialogContext();
  const queryClient = useQueryClient();

  const deleteCaseNoteMutation = useMutation({
    mutationFn: (noteId: string) =>
      caseNotesApi.deleteCaseNote({
        clientId: client.clientId,
        noteId,
      }),

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.ClientCaseNotes, client.clientId] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.ClientOverview, client.clientId] });
      showSuccess(t("clients.client.caseNotes.deleteDialog.successfullyDeleted"));
      onDeleted();
    },
    onError: () => {
      showError(t("clients.client.caseNotes.deleteDialog.deleteFailed"));
    },
  });

  const handleDelete = (caseNote: CaseNoteResponse) => {
    showYesNoDialog(
      t("clients.client.caseNotes.deleteDialog.title"),
      t("clients.client.caseNotes.deleteDialog.text"),
      (confirmed) => {
        if (confirmed) {
          deleteCaseNoteMutation.mutate(caseNote.caseNoteId);
        }
      }
    );
  };

  return { handleDelete, isDeleting: deleteCaseNoteMutation.isPending };
}
